import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

/**
 * L'ACCES de l'utilisateur courant, tel que le gabarit et le shell le lisent.
 *
 * LA SECURITE REELLE EST DANS LES RULES (`firebase/firestore.rules`) : ce
 * module ne fait que MASQUER et GRISER, jamais proteger. Il repete, en JS, la
 * regle que les rules appliquent en base, pour que l'ecran dise la verite
 * avant que la base ne refuse : un bouton grise avec sa raison vaut mieux
 * qu'une ecriture refusee apres coup. Si les deux divergent, ce sont les rules
 * qui ont raison, et c'est ici qu'on corrige.
 *
 * LE MODELE, en trois etages :
 *   1. le ROLE d'org : `owner`, `admin` (tout, plus la gestion des membres),
 *      `member` (ecrit partout par defaut), `viewer` (lit partout par defaut) ;
 *   2. l'ACCES PAR PORTE, une porte = une famille du SDK + un `type`. Les
 *      derogations sont rangees PAR FAMILLE puis par type, la clef `*` valant
 *      pour toute la famille et pour les documents sans `type` :
 *      `portes: { assets: { materiel: 'read' }, contacts: { '*': 'none' } }`.
 *      Elles ne s'ecrivent que pour DEROGER au defaut du role. Cette forme
 *      n'est pas un choix de style : les rules ne peuvent lire le `type` d'un
 *      document que si la requete le fixe, donc elles regardent d'abord la
 *      famille, puis `*`, et le type en dernier ;
 *   3. l'ACCES PAR APP, une commodite de l'ecran des utilisateurs : l'app se
 *      traduit en portes au moment ou on enregistre. Les rules ne lisent que
 *      les portes.
 * Un membre `disabled` n'a plus rien, quel que soit son role.
 *
 * POURQUOI UN PORT, comme les modeles de fiches (`lib/templates.tsx`) :
 * `@taki/ui` ne parle jamais au SDK. L'hote lit la fiche membre par
 * `sdk.members` et remplit cette forme ; le gabarit et le shell la lisent par
 * contexte. Sans provider, tout est permis : c'est l'etat d'avant ce chantier,
 * et les rules, elles, refusent quand meme.
 */

/** Les trois niveaux, du plus ferme au plus ouvert. */
export type NiveauAcces = 'none' | 'read' | 'write';

/** Les roles d'org, alignes sur `orgRoleSchema` de `@taki/data-model`. */
export type RoleOrg = 'owner' | 'admin' | 'member' | 'viewer';

/** Les derogations d'une famille : par type, `*` pour toute la famille. */
export type DerogationsDeFamille = Record<string, NiveauAcces>;

/** Ce que la fiche membre porte, dans la forme que ce module lit (structurel, pas importe). */
export interface FicheMembre {
  orgRole: RoleOrg;
  disabled?: boolean;
  archivedAt?: unknown;
  deletedAt?: unknown;
  /** Derogations par famille puis par type. */
  portes?: Record<string, DerogationsDeFamille>;
  /** Choix par app, la commodite de l'ecran ; derive en portes a l'enregistrement. */
  apps?: Record<string, NiveauAcces>;
}

/** Ordre des niveaux, pour comparer : `RANG.write > RANG.read > RANG.none`. */
export const RANG: Readonly<Record<NiveauAcces, number>> = Object.freeze({
  none: 0,
  read: 1,
  write: 2,
});

/** La clef de type d'un document sans `type` : `*`, comme dans les rules. */
export const TYPE_JOKER = '*';

/**
 * Le niveau que le ROLE donne par defaut, sans derogation. Un role inconnu ou
 * absent vaut `member` : c'est l'etat d'un compte provisionne par claims seuls,
 * avant ce chantier, et il ne doit rien perdre.
 */
export function niveauParDefaut(role: RoleOrg | null | undefined): NiveauAcces {
  return role === 'viewer' ? 'read' : 'write';
}

/**
 * Le niveau EFFECTIF sur une porte, LA regle que les rules appliquent, dans le
 * meme ordre : pas de fiche -> le defaut du role porte par les claims ;
 * fiche desactivee -> rien ; owner ou admin -> tout ; la famille n'a aucune
 * derogation -> le defaut du role ; une derogation `*` sur la famille -> elle ;
 * pas de type connu -> rien ; une derogation sur le type -> elle ; sinon le
 * defaut du role.
 *
 * « Pas de type connu vaut rien » vient des rules : sur une requete de liste,
 * elles ne peuvent pas lire le type d'un document que la requete ne fixe pas,
 * et retomber sur le defaut du role laisserait passer ce que la derogation
 * ferme. Une famille a derogations par type s'interroge donc TOUJOURS avec le
 * type (les portes du socle le font), et un document sans type n'y est
 * lisible que par `*`.
 */
export function niveauEffectif(
  fiche: FicheMembre | null,
  roleDesClaims: RoleOrg | null | undefined,
  famille: string,
  type?: string | null,
): NiveauAcces {
  if (fiche === null) return niveauParDefaut(roleDesClaims);
  if (fiche.disabled === true || fiche.archivedAt != null || fiche.deletedAt != null) return 'none';
  if (fiche.orgRole === 'owner' || fiche.orgRole === 'admin') return 'write';
  const derogations = fiche.portes?.[famille];
  if (derogations === undefined) return niveauParDefaut(fiche.orgRole);
  const surLaFamille = derogations[TYPE_JOKER];
  if (surLaFamille !== undefined) return surLaFamille;
  if (type === undefined || type === null || type === '') return 'none';
  const surLeType = derogations[type];
  if (surLeType !== undefined) return surLeType;
  return niveauParDefaut(fiche.orgRole);
}

/** Role et droit d'administration visibles, fermes en cas de panne ou de fiche inactive. */
export function autorisationDeMembre(
  fiche: FicheMembre | null,
  roleDesClaims: RoleOrg | null | undefined,
  indisponible: boolean,
): { role: RoleOrg | null; peutGererLesMembres: boolean } {
  const inactive =
    fiche != null &&
    (fiche.disabled === true || fiche.archivedAt != null || fiche.deletedAt != null);
  const role = inactive || indisponible ? null : (fiche?.orgRole ?? roleDesClaims ?? null);
  return {
    role,
    peutGererLesMembres: !inactive && !indisponible && (role === 'owner' || role === 'admin'),
  };
}

/** Une session connectee reste fermee pendant le chargement et apres une panne. */
export function accesMembreIndisponible(
  email: string | null,
  pret: boolean,
  erreur: string | null,
): boolean {
  return email !== null && (!pret || erreur !== null);
}

/** `a` vaut au moins `b`. */
export function auMoins(a: NiveauAcces, b: NiveauAcces): boolean {
  return RANG[a] >= RANG[b];
}

/** Une fiche membre telle que l'ecran des utilisateurs la voit (structurel, pas importe). */
export interface MembreVue {
  email: string;
  displayName?: string | null;
  userId?: string | null;
  /**
   * L'identifiant STABLE de la personne, celui que les fiches metier stockent
   * (un champ `personne`). Tire au sort a la creation, jamais affiche, et il ne
   * change pas avec l'adresse. Absent sur une fiche d'avant l'annuaire des
   * personnes, tant que le rattrapage n'est pas passe.
   */
  personId?: string | null;
  orgRole: RoleOrg;
  disabled: boolean;
  archivedAt?: unknown;
  deletedAt?: unknown;
  apps: Record<string, NiveauAcces>;
  portes: Record<string, DerogationsDeFamille>;
}

/**
 * Ce que rend un retrait.
 *
 * ARCHIVER LA FICHE SUFFIT A FERMER LA PORTE : les rules ne donnent plus rien a
 * une fiche archivee, tout de suite. Couper les SESSIONS deja ouvertes, en
 * revanche, demande une Cloud Function, et une Function peut ne pas etre
 * deployee. L'ecran doit donc pouvoir dire « c'est retire, mais une session
 * ouverte ailleurs vivra encore une heure » plutot que de laisser croire a
 * l'un ou a l'autre.
 */
export interface RetraitMembre {
  /** Les sessions ouvertes et les claims sont-ils tombes, ici et maintenant ? */
  sessionsCoupees: boolean;
  /** Ce qui a empeche de les couper, quand ca a echoue. */
  raison: string | null;
}

/**
 * Ce que rend un changement d'adresse : la nouvelle fiche, et le sort de
 * l'ANCIEN compte. Meme partage que le retrait : la nouvelle fiche et
 * l'archivage de l'ancienne sont des ecritures Firestore et suffisent a fermer
 * l'ancienne porte ; retirer les claims de l'ancien compte demande une Cloud
 * Function, qui peut manquer, et l'ecran doit pouvoir le dire.
 */
export interface ChangementDAdresse extends RetraitMembre {
  /** La fiche a la nouvelle adresse. */
  membre: MembreVue;
}

/** Ce que rend un geste qui pose un mot de passe. */
export interface MotDePasseMembre {
  /**
   * Le mot de passe, MONTRE UNE SEULE FOIS. Il n'existe nulle part ailleurs :
   * ni en base, ni dans un journal. Non montre = perdu.
   */
  motDePasse: string;
  /** Le compte vient-il d'etre cree par ce geste ? */
  compteCree: boolean;
}

/**
 * La GESTION des membres, fournie par l'hote sur `sdk.members` et `sdk.comptes`.
 * Les rules refusent tout a qui n'est pas owner ou admin : le port n'est qu'un
 * guichet, l'ecran le grise avec `RAISONS_ACCES.gestionReservee` pour les autres.
 *
 * DEUX CHOSES DIFFERENTES VIVENT ICI, et les confondre est la faute a ne pas
 * faire :
 *   - la FICHE, c'est-a-dire les DROITS (`ajouter`, `modifier`). Elle suffit a
 *     INVITER quelqu'un : sa fiche l'attend, il entrera avec Google, et la
 *     porte le reconnaitra a son adresse ;
 *   - le COMPTE, c'est-a-dire de quoi entrer SANS Google (`motDePasse`). C'est
 *     le deuxieme chemin, pas le premier, et il passe par une Cloud Function
 *     parce qu'un navigateur ne cree pas un compte Firebase Auth.
 */
export interface MembresPort {
  lister(): Promise<MembreVue[]>;
  /** Invite une adresse : la FICHE seule. La personne entrera avec Google. */
  ajouter(entree: {
    email: string;
    displayName?: string;
    orgRole: RoleOrg;
    apps?: Record<string, NiveauAcces>;
    portes?: Record<string, DerogationsDeFamille>;
  }): Promise<MembreVue>;
  modifier(
    email: string,
    patch: Partial<Pick<MembreVue, 'displayName' | 'orgRole' | 'disabled' | 'apps' | 'portes'>>,
  ): Promise<MembreVue>;
  /** Retire un membre : la fiche est archivee, les sessions coupees si possible. */
  retirer(email: string): Promise<RetraitMembre>;
  /**
   * Tire un mot de passe pour cette adresse, et CREE son compte s'il manquait.
   * `null` quand l'hote ne branche pas les comptes (pas de Cloud Functions) :
   * l'ecran grise alors le geste avec sa raison, il ne le cache pas (G16).
   */
  motDePasse?: ((email: string) => Promise<MotDePasseMembre>) | undefined;
  /**
   * CHANGER L'ADRESSE d'une personne sans lui faire perdre ce qui la designe :
   * une nouvelle fiche au MEME `personId` (memes role, portes, apps, nom),
   * l'ancienne archivee, l'annuaire des personnes suit. Les heures et les fiches
   * qui la citent restent a elle, parce qu'elles stockent son `personId` et non
   * son adresse. Elle entrera avec la nouvelle adresse.
   *
   * ABSENT quand l'hote ne le branche pas : l'ecran grise le geste avec sa
   * raison, il ne le cache pas (G16).
   */
  changerAdresse?: ((email: string, nouvelle: string) => Promise<ChangementDAdresse>) | undefined;
}

/** Ce que l'hote fournit au socle. */
export interface AccesPort {
  /** L'email de l'utilisateur courant, en minuscules, ou null. */
  email: string | null;
  /** La fiche est lue (ou son absence constatee) : avant, on ne grise rien. */
  pret: boolean;
  /** La fiche n'a pas pu etre lue : acces ferme, distinct d'une absence constatee. */
  indisponible?: boolean;
  /** Message montrable a l'utilisateur quand `indisponible` vaut vrai. */
  erreur?: string | null;
  /** Relance la lecture de la fiche membre. */
  reessayer?(): void;
  /** Le role effectif : celui de la fiche, sinon celui des claims, sinon null. */
  role: RoleOrg | null;
  /** Le niveau sur une porte de l'utilisateur courant. */
  niveau(famille: string, type?: string | null): NiveauAcces;
  /** Le niveau sur une app (le choix par app, sinon le defaut du role). */
  niveauApp(appId: string): NiveauAcces;
  /** Peut gerer les membres : owner ou admin. */
  peutGererLesMembres: boolean;
  /** Le guichet des membres ; `null` quand l'hote ne le fournit pas. */
  membres: MembresPort | null;
}

/**
 * La raison de griser un geste d'ECRITURE sur la porte affichee, ou `null`
 * quand il reste vivant. Sans port, sans fiche lue ou sans jeu declare :
 * `null`, on ne grise pas sur une absence d'information.
 */
export function raisonDeLectureSeule(
  port: AccesPort | null,
  jeu: { family: string; type?: string | null } | null | undefined,
): string | null {
  if (port === null || !port.pret || jeu === null || jeu === undefined || jeu.family === '')
    return null;
  const niveau = port.niveau(jeu.family, jeu.type);
  if (niveau === 'none') return RAISONS_ACCES.aucunAcces;
  if (niveau === 'read') return RAISONS_ACCES.lectureSeule;
  return null;
}

/** Les phrases des grisements, ecrites une fois pour le gabarit et le shell. */
export const RAISONS_ACCES = Object.freeze({
  lectureSeule: "Lecture seule sur cette porte : ton acces ne permet pas d'ecrire.",
  aucunAcces: 'Aucun acces a cette porte.',
  appSansAcces: 'Aucun acces a cette app.',
  gestionReservee: 'La gestion des utilisateurs est reservee aux administrateurs.',
});

const AccesContext = createContext<AccesPort | null>(null);

export function AccesProvider({ value, children }: { value: AccesPort; children: ReactNode }) {
  return <AccesContext.Provider value={value}>{children}</AccesContext.Provider>;
}

/** Le port, ou `null` quand l'hote n'en fournit pas (rien n'est grise). */
export function useAccesPort(): AccesPort | null {
  return useContext(AccesContext);
}

/**
 * Peut-on ECRIRE sur cette porte ? Sans port, ou tant que la fiche n'est pas
 * lue, oui : on ne grise jamais sur une absence d'information.
 */
export function peutEcrireSurPorte(
  port: AccesPort | null,
  famille: string | null | undefined,
  type?: string | null,
): boolean {
  if (port === null || !port.pret || famille === undefined || famille === null || famille === '')
    return true;
  return auMoins(port.niveau(famille, type), 'write');
}
